import { useCallback, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { AlarmClockIcon, DownloadIcon, SmartphoneIcon } from "lucide-react";

import SearchParams from "simplydo/core/searchParams";
import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Divider } from "simplydo/react/ui/divider";
import { Icon } from "simplydo/react/ui/icon";
import { Loader } from "simplydo/react/ui/loader";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import { type UrlOrganisation, organisationLogoUrl } from "./types";

const StyledLoader = styled(Loader)`
  margin-bottom: 15px;
`;

const OrgLogo = styled.img`
  width: 100px;
  margin-bottom: 15px;
  object-fit: contain;
`;

const OrgText = styled.span`
  display: block;
  text-align: center;
  font-weight: bold;
  font-size: 1.2em;
`;

const AppInfoText = styled.p`
  text-align: center;
  margin: 10px 2rem 15px;
  font-size: 1.1em;
  color: #696969;
`;

const SmartLink = () => {
  const user = useCurrentUser();
  const userAgent = util.getMobileOperatingSystem();
  const isMobileDevice = userAgent === "Android" || userAgent === "iOS";
  const appLink =
    userAgent === "Android"
      ? "https://play.google.com/store/apps/details?id=uk.co.simplydo.mobile&hl=en_US"
      : "https://apps.apple.com/gb/app/simply-do/id1445564123";
  const code = util.code() || "simplydo";
  const [goToAppStore, setGoToAppStore] = useState(false);
  const [organisation, setOrganisation] = useState<UrlOrganisation | null>(
    null,
  );
  const userId = user?._id;

  const { t } = useTranslation();
  const navigate = useNavigate();

  // https://stackoverflow.com/questions/13044805/how-to-check-if-an-app-is-installed-from-a-web-page-on-an-iphone
  const redirectToAppStore = useCallback(
    (exchangeToken?: string) => {
      // Checks for code execution during timeout i.e if the webpage has been open. Accounts for if user went to the app then immediately came back to the browser
      const now = Date.now();
      setTimeout(() => {
        if (Date.now() - now > 100) return;
        setGoToAppStore(true);
      }, 50);
      const params = new SearchParams({ org: code, token: exchangeToken });
      window.location.href = `simplydo://${params.toSafeString()}`;
    },
    [code],
  );

  const getOrg = useCallback(() => {
    api.organisations.getByCode(
      code,
      (org) => {
        if (org) {
          setOrganisation(org as UrlOrganisation);
        }
      },
      () => {},
    );
  }, [code]);

  const checkDevice = useCallback(() => {
    if (!isMobileDevice) {
      navigate("/");
    } else if (userId) {
      api.auth.generateExchangeToken(
        (data) => {
          const token =
            typeof data.exchangeToken === "string"
              ? data.exchangeToken
              : undefined;
          redirectToAppStore(token);
        },
        () => {},
      );
    } else {
      redirectToAppStore();
    }
  }, [isMobileDevice, redirectToAppStore, navigate, userId]);

  useEffect(() => {
    checkDevice();
    getOrg();
  }, [checkDevice, getOrg]);

  const logoUrl = organisationLogoUrl(organisation);

  return (
    <div>
      {goToAppStore ? (
        <>
          {logoUrl && (
            <div style={{ textAlign: "center" }}>
              <OrgLogo src={logoUrl} />
              <OrgText>
                {organisation?.name} is using {util.appName()}
              </OrgText>
            </div>
          )}
          <Divider />
          <AppInfoText>
            <p>
              <Icon icon={DownloadIcon} /> Download the {util.appName()} mobile
              app for an optimised experience on your mobile devices.
            </p>
            <p>
              <Icon icon={SmartphoneIcon} /> Create and view{" "}
              {t("generic.ideas")}, message team members or browse{" "}
              {t("generic.challenges")} quickly and easily.
            </p>
            <p>
              <Icon icon={AlarmClockIcon} /> Plus you will be able to receive
              updates via push notifications.
            </p>
          </AppInfoText>
          <Divider hidden />
          <Button
            onClick={() => {
              window.location.href = appLink;
            }}
            style={{ marginBottom: 15 }}
            color="primary"
          >
            Visit {util.appName()} on the App Store
          </Button>
          <Button size="small" onClick={() => navigate("/")}>
            Return to {util.appName()} Web
          </Button>
        </>
      ) : (
        <StyledLoader />
      )}
    </div>
  );
};

export default SmartLink;
